/**
 * Crear o editar una colección: su nombre, descripción, imagen y qué entra.
 *
 * Dos maneras, como en Shopify:
 * - Manual: se marcan las ofertas, y el orden es el de la lista.
 * - Automática: reglas sobre lo que ya tienen las ofertas (tipo, precio,
 *   etiqueta, título, campos propios como «Marca»). Las nuevas que las cumplan
 *   entran solas. Debajo se ve en vivo cuáles entran hoy.
 */
import { matchesSearch } from '@webbuilder/shared-types'
import { CurrencySymbol } from './CurrencySymbol'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconCategory, IconHandFinger, IconLoader2, IconPhoto, IconPlus, IconSearch, IconTrash, IconWand, IconX } from '@tabler/icons-react'
import type { OfferFields } from '@webbuilder/schemas'
import { useI18n, translateServerError } from '../../../lib/i18n'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { minorUnitFactor } from './offerShared'
import { knownBrands, knownTags, OfferBrandField, OfferTagsField } from './OfferBrandField'
import { useOfferLabels } from '../../../lib/offerLabels'
import {
  createCollection, previewCollection, updateCollection,
  type CollectionInput, type CollectionRule, type CollectionRuleField, type CollectionRuleOp, type CollectionSort, type Offer, type OfferCollection,
} from '../../../lib/api'

const SORTS: CollectionSort[] = ['manual', 'newest', 'bestselling', 'priceAsc', 'priceDesc', 'title']
const KINDS = ['products', 'digital', 'services', 'bookings'] as const

/** El tipo de una oferta, como lo cuenta el catálogo. */
function offerKind(offer: Offer): (typeof KINDS)[number] {
  if (offer.booking?.durationMinutes) return 'bookings'
  if (offer.type === 'digital') return 'digital'
  if (offer.type === 'physical') return 'products'
  return 'services'
}

/**
 * Lo que se ve en el selector de comparación. El precio suma «es gratis» y «es
 * de pago», que se guardan como la regla `free` (el servidor ya la entiende),
 * y la disponibilidad se lee entera en la comparación: «está disponible» /
 * «está agotado», sin un tercer campo vacío.
 */
type UiOp = CollectionRuleOp | 'free' | 'paid'
const PRICE_OPS: UiOp[] = ['lt', 'gt', 'is', 'isNot', 'free', 'paid']

/** El campo y la comparación tal como se enseñan. */
function uiOf(rule: CollectionRule): { field: CollectionRuleField; op: UiOp } {
  if (rule.field === 'free') return { field: 'price', op: rule.op === 'isNot' ? 'paid' : 'free' }
  return { field: rule.field, op: rule.op }
}

/** Una comparación sin valor detrás: ocupa también el hueco del valor. */
const opIsWhole = (field: CollectionRuleField, op: UiOp) => field === 'stock' || op === 'free' || op === 'paid'

/** Qué comparaciones tiene cada campo. */
function opsFor(field: CollectionRuleField): CollectionRuleOp[] {
  if (field === 'price') return ['lt', 'gt', 'is', 'isNot']
  if (field === 'kind' || field === 'free' || field === 'stock' || field === 'tag') return ['is', 'isNot']
  if (field === 'brand') return ['is', 'isNot']
  return ['is', 'contains', 'notContains', 'isNot']
}

export function CollectionDialog({ projectId, token, collection, offers, offerFields, currency, onClose, onSaved, onLabelRemoved }: {
  projectId: string
  token: string
  collection: OfferCollection | null
  offers: Offer[]
  offerFields: OfferFields
  currency: string
  onClose: () => void
  onSaved: (collection: OfferCollection) => void
  /** Se eliminó una marca o etiqueta de todo el proyecto: las ofertas y colecciones de la pantalla cambiaron. */
  onLabelRemoved?: () => void
}) {
  const { t } = useI18n()
  const [title, setTitle] = useState(collection?.title ?? '')
  const [description, setDescription] = useState(collection?.description ?? '')
  const [image, setImage] = useState(collection?.image ?? '')
  const [kind, setKind] = useState<'manual' | 'auto'>(collection?.kind ?? 'manual')
  const [match, setMatch] = useState<'all' | 'any'>(collection?.match ?? 'all')
  const [rules, setRules] = useState<CollectionRule[]>(collection?.rules.length ? collection.rules : [{ field: 'tag', op: 'is', value: '' }])
  const [sort, setSort] = useState<CollectionSort>(collection?.sort ?? 'manual')
  const [picked, setPicked] = useState<string[]>(collection?.kind === 'manual' ? collection.offerIds : [])
  const [query, setQuery] = useState('')
  const [preview, setPreview] = useState<string[] | null>(collection?.offerIds ?? null)
  const [picking, setPicking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const factor = minorUnitFactor(currency)

  /* Los campos propios de todos los tipos, sin repetir («Marca» puede estar en dos). */
  const customFields = useMemo(() => {
    const seen = new Map<string, string>()
    for (const list of Object.values(offerFields)) for (const field of list ?? []) if (field.type === 'text' && !seen.has(field.id)) seen.set(field.id, field.label)
    return [...seen.entries()].map(([id, label]) => ({ id, label }))
  }, [offerFields])
  /* La lista compartida del proyecto (también las que ninguna oferta lleva aún) y las de las ofertas. */
  const labels = useOfferLabels(projectId, token)
  const allTags = useMemo(() => [...new Set([...labels.tags, ...knownTags(offers)].map((tag) => tag.name))].sort((a, b) => a.localeCompare(b)), [labels.tags, offers])
  const allBrands = useMemo(() => [...new Set([...labels.brands, ...knownBrands(offers)].map((brand) => brand.name))].sort((a, b) => a.localeCompare(b)), [labels.brands, offers])

  const input = (): CollectionInput => ({
    title: title.trim(), description: description.trim(), image, kind, match,
    rules: rules.filter((rule) => rule.value.trim() || rule.field === 'free' || rule.field === 'stock'),
    sort, offerIds: picked,
  })

  /* La vista previa: en vivo, medio segundo después del último cambio. */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      /* Sólo lo que decide qué entra: con el nombre vacío (una colección nueva)
         el servidor rechazaba la petición y se quedaba «Calculando…». */
      const { kind: k, match: m, rules: r, sort: o, offerIds } = input()
      previewCollection(projectId, { kind: k, match: m, rules: r, sort: o, offerIds }, token)
        .then((result) => setPreview(result.offerIds))
        .catch(() => setPreview([]))
    }, 450)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token, kind, match, rules, sort, picked])

  const save = async () => {
    if (!title.trim()) { setError(t('collections.form.needTitle')); return }
    setSaving(true); setError('')
    try {
      onSaved(collection ? await updateCollection(projectId, collection.id, input(), token) : await createCollection(projectId, input(), token))
    } catch (err) {
      setError(translateServerError(err, t('error.unexpected')))
    } finally {
      setSaving(false)
    }
  }

  /* Eliminada de todo el proyecto: el servidor ya la quitó de las ofertas y de
     las condiciones guardadas; aquí se quita de las que se están editando. */
  const labelRemoved = (field: 'brand' | 'tag', name: string) => {
    const lower = name.toLocaleLowerCase()
    setRules((current) => current.map((rule) => (rule.field === field && rule.value.toLocaleLowerCase() === lower ? { ...rule, value: '' } : rule)))
    onLabelRemoved?.()
  }

  /* Elegir una comparación del selector: «gratis» y «de pago» pasan la regla a `free`. */
  const pickOp = (index: number, op: UiOp) => setRules((current) => current.map((rule, i) => {
    if (i !== index) return rule
    if (op === 'free' || op === 'paid') return { field: 'free', op: op === 'free' ? 'is' : 'isNot', value: '' }
    if (rule.field === 'free') return { field: 'price', op, value: '' }
    return { ...rule, op }
  }))

  const opLabel = (field: CollectionRuleField, op: UiOp) => field === 'price'
    ? t(`collections.opPrice.${op}`)
    : field === 'stock' ? t(`collections.opStock.${op}`) : t(`collections.op.${op}`)

  const patchRule = (index: number, patch: Partial<CollectionRule>) => setRules((current) => current.map((rule, i) => {
    if (i !== index) return rule
    const next = { ...rule, ...patch }
    if (patch.field && !opsFor(patch.field).includes(next.op)) next.op = opsFor(patch.field)[0]!
    if (patch.field) next.value = patch.field === 'kind' ? 'products' : patch.field === 'brand' ? allBrands[0] ?? '' : patch.field === 'tag' ? allTags[0] ?? '' : ''
    return next
  }))

  const byId = new Map(offers.map((offer) => [offer.id, offer]))
  const shown = offers.filter((offer) => matchesSearch(offer.title, query))

  const offerRow = (offer: Offer) => (
    <>
      <span className="dash-collection-thumb" aria-hidden="true">{offer.images[0] ? <img src={offer.images[0]} alt="" /> : <IconPhoto size={14} />}</span>
      <span className="dash-collection-offer-copy">
        <strong>{offer.title}</strong>
        <small>{t(`collections.kindValue.${offerKind(offer)}`)}{offer.status !== 'active' ? ` · ${t('collections.form.draft')}` : ''}</small>
      </span>
    </>
  )

  /* El mismo marco que «Nueva cita»: una superficie, tres columnas separadas
     por un filo, títulos de columna en negrita y el estado en el pie. */
  return createPortal(
    <div className="dash-modal-backdrop dash-new-booking-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose() }}>
      <section className="dash-new-booking-dialog dash-collection-modal" role="dialog" aria-modal="true" aria-labelledby="collection-dialog-title">
        <header className="dash-new-booking-head">
          <div className="dash-new-booking-head-copy">
            <h2 id="collection-dialog-title">{t(collection ? 'collections.form.editTitle' : 'collections.form.newTitle')}</h2>
            <p>{t('collections.form.lead')}</p>
          </div>
          <button type="button" className="dash-create-close" aria-label={t('collections.form.close')} disabled={saving} onClick={onClose}><IconX size={18} /></button>
        </header>

        <div className="dash-new-booking-content dash-collection-content">
          {/* Izquierda: lo que la describe. */}
          <aside className="dash-new-booking-service dash-collection-side">
            <button type="button" className="dash-collection-image" onClick={() => setPicking(true)} aria-label={t('collections.form.image')}>
              {image ? <img src={image} alt="" /> : <span><IconPhoto size={22} />{t('collections.form.image')}</span>}
            </button>
            <label className="dash-new-booking-field">
              <span>{t('collections.form.name')}<small>{t('collections.form.required')}</small></span>
              <input value={title} maxLength={120} placeholder={t('collections.form.namePlaceholder')} onChange={(event) => setTitle(event.target.value)} autoFocus={!collection} />
            </label>
            <label className="dash-new-booking-field">
              <span>{t('collections.form.description')}<small>{t('collections.form.optional')}</small></span>
              <textarea value={description} maxLength={2000} rows={3} placeholder={t('collections.form.descriptionPlaceholder')} onChange={(event) => setDescription(event.target.value)} />
            </label>
          </aside>

          {/* En medio: cómo se arma. */}
          <section className="dash-new-booking-section dash-collection-main">
            <header><strong>{t('collections.form.type')}</strong></header>
            <div className="dash-new-booking-section-body">
              <div className="dash-new-booking-field">
                {/* El mismo selector que el asistente de servicios: dos opciones y
                    debajo, en gris, qué hace la elegida. */}
                <div className={`dash-svc-segmented${kind === 'auto' ? ' is-second' : ''}`} role="radiogroup" aria-label={t('collections.form.type')}>
                  {(['manual', 'auto'] as const).map((value) => (
                    <button key={value} type="button" role="radio" aria-checked={kind === value} className={kind === value ? 'is-on' : ''} onClick={() => setKind(value)}>
                      {value === 'manual' ? <IconHandFinger size={14} /> : <IconWand size={14} />} {t(`collections.kind.${value}`)}
                    </button>
                  ))}
                </div>
                <small className="dash-collection-hint">{t(`collections.kind.${kind}Hint`)}</small>
              </div>

              {kind === 'auto' ? (
                <div className="dash-new-booking-field">
                  <span>{t('collections.form.conditions')}</span>
                  <div className="dash-collection-match">
                    <span>{t('collections.form.matchLead')}</span>
                    <select value={match} onChange={(event) => setMatch(event.target.value === 'any' ? 'any' : 'all')}>
                      <option value="all">{t('collections.match.all')}</option>
                      <option value="any">{t('collections.match.any')}</option>
                    </select>
                  </div>
                  <div className="dash-collection-rules">
                    {rules.map((rule, index) => {
                      const ui = uiOf(rule)
                      const whole = opIsWhole(ui.field, ui.op)
                      return (
                      <div className="dash-collection-rule" key={index}>
                        <select value={ui.field} onChange={(event) => patchRule(index, { field: event.target.value as CollectionRuleField })}>
                          {(['tag', 'brand', 'kind', 'price', 'title', 'stock'] as const).map((field) => <option key={field} value={field}>{t(`collections.field.${field}`)}</option>)}
                          {customFields.map((field) => <option key={field.id} value={`field:${field.id}`}>{field.label}</option>)}
                        </select>
                        <select className={whole ? 'is-whole' : undefined} value={ui.op} onChange={(event) => pickOp(index, event.target.value as UiOp)}>
                          {(ui.field === 'price' ? PRICE_OPS : opsFor(ui.field)).map((op) => <option key={op} value={op}>{opLabel(ui.field, op)}</option>)}
                        </select>
                        {whole ? null : rule.field === 'brand' || rule.field === 'tag' ? (
                          /* Las que ya llevan tus ofertas, o una nueva escrita aquí mismo. */
                          /* El mismo campo que en la ficha de la oferta: elegir, crear
                             o eliminar de todo el proyecto, con la lista compartida. */
                          <div className="dash-collection-rule-picker">
                            {rule.field === 'brand' ? (
                              <OfferBrandField projectId={projectId} token={token} value={rule.value} onChange={(value) => patchRule(index, { value })} onRemovedEverywhere={(name) => labelRemoved('brand', name)} />
                            ) : (
                              <OfferTagsField projectId={projectId} token={token} max={1} value={rule.value ? [rule.value] : []} onChange={(tags) => patchRule(index, { value: tags[0] ?? '' })} onRemovedEverywhere={(name) => labelRemoved('tag', name)} />
                            )}
                          </div>
                        ) : rule.field === 'kind' ? (
                          <select value={rule.value} onChange={(event) => patchRule(index, { value: event.target.value })}>
                            {KINDS.map((value) => <option key={value} value={value}>{t(`collections.kindValue.${value}`)}</option>)}
                          </select>
                        ) : rule.field === 'price' ? (
                          <span className="dash-collection-price">
                            <CurrencySymbol currency={currency} className="dash-collection-price-symbol" />
                            <input aria-label={`${t('collections.field.price')} (${currency.toUpperCase()})`} inputMode="decimal" value={rule.value ? String(Number(rule.value) / factor) : ''} onChange={(event) => patchRule(index, { value: event.target.value.trim() ? String(Math.round(Number(event.target.value) * factor) || 0) : '' })} />
                            <span aria-hidden="true">{currency.toUpperCase()}</span>
                          </span>
                        ) : (
                          <input value={rule.value} maxLength={200} placeholder={t('collections.form.valuePlaceholder')} onChange={(event) => patchRule(index, { value: event.target.value })} />
                        )}
                        <button type="button" className="dash-icon-btn" aria-label={t('collections.form.removeRule')} disabled={rules.length <= 1} onClick={() => setRules((current) => current.filter((_, i) => i !== index))}><IconTrash size={14} /></button>
                      </div>
                      )
                    })}
                    {rules.length < 20 ? (
                      <button type="button" className="dash-secondary-btn dash-collection-add-rule" onClick={() => setRules((current) => [...current, { field: 'tag', op: 'is', value: '' }])}>
                        <IconPlus size={14} /> {t('collections.form.addRule')}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="dash-new-booking-field">
                  <span>{t('collections.form.offers')}<small>{t('collections.form.picked', { count: picked.length })}</small></span>
                  <div className="dash-collection-offers">
                    <span className="dash-collection-search">
                      <IconSearch size={14} aria-hidden="true" />
                      <input value={query} placeholder={t('collections.form.search')} onChange={(event) => setQuery(event.target.value)} />
                    </span>
                    <ul className="dash-collection-offer-list">
                      {shown.length === 0 ? <li className="dash-collection-offer-empty">{t('collections.form.noOffers')}</li> : shown.map((offer) => {
                        const on = picked.includes(offer.id)
                        return (
                          <li key={offer.id}>
                            <label className={on ? 'is-on' : ''}>
                              <input type="checkbox" checked={on} onChange={() => setPicked((current) => current.includes(offer.id) ? current.filter((id) => id !== offer.id) : [...current, offer.id])} />
                              {offerRow(offer)}
                              {on ? <em className="dash-collection-order">{picked.indexOf(offer.id) + 1}</em> : null}
                            </label>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                </div>
              )}

            </div>
          </section>

          {/* Derecha: lo que entra hoy, con su propio scroll. Así se ve en vivo
              lo que hacen las condiciones. */}
          <section className="dash-new-booking-section dash-collection-preview">
            <header>
              <strong>{t('collections.form.inCollection')}</strong>
              {preview ? <small>{preview.length}</small> : null}
            </header>
            {/* El orden, junto a lo que ordena: se ve cambiar la lista al elegirlo. */}
            <label className="dash-collection-sort">
              <span>{t('collections.form.sort')}</span>
              <select value={sort} onChange={(event) => setSort(event.target.value as CollectionSort)}>
                {SORTS.map((value) => <option key={value} value={value}>{t(`collections.sort.${value}`)}</option>)}
              </select>
            </label>
            {preview && preview.length ? (
              <ul className="dash-collection-preview-rows">
                {preview.map((id, index) => {
                  const offer = byId.get(id)
                  if (!offer) return null
                  return <li key={id}><em>{index + 1}</em>{offerRow(offer)}</li>
                })}
              </ul>
            ) : <p className="dash-collection-preview-empty">{preview ? t('collections.form.previewEmpty') : t('collections.form.previewLoading')}</p>}
          </section>
        </div>

        {error ? <p className="dash-new-booking-error" role="alert">{error}</p> : null}

        <footer className="dash-new-booking-foot">
          <span className={title.trim() && preview ? 'has-slot' : ''}>
            {!title.trim() ? t('collections.form.footNeedName') : preview ? t('collections.form.previewCount', { count: preview.length }) : t('collections.form.previewLoading')}
          </span>
          <div>
            <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('collections.form.cancel')}</button>
            <button type="button" className="dash-primary-btn" disabled={saving || !title.trim()} onClick={() => { void save() }}>
              {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCategory size={14} />}
              {saving ? t('collections.form.saving') : t(collection ? 'collections.form.save' : 'collections.form.create')}
            </button>
          </div>
        </footer>
      </section>
      {picking ? (
        <ImageLibraryPickerDialog token={token} projectId={projectId} onSelect={(url) => { setImage(url); setPicking(false) }} onClose={() => setPicking(false)} />
      ) : null}
    </div>,
    document.body,
  )
}
